import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ChoiceGroup, Checkbox, Label } from '@bingads-webui-react/fabric-control';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { SeasonalityCampaignSelector, validator as campaignSelectionValidator } from './seasonality-campaign-selector';
import { Error } from './error';
import { AdjustmentType, isDataExclusion } from '../adjustment-type';

function getStyles() {
  return {
    scopeChoices: {
      marginBottom: '10px',
    },
    scopeCheckbox: {
      marginLeft: '30px',
      '& div[role=alert]': {
        paddingTop: 5,
      },
    },
  };
}

const getErrorMessage = (selectedCampaignTypes, selectedScope) => {
  if (selectedScope === 'type') {
    if (_.isEmpty(selectedCampaignTypes)) {
      return _TL_('Campaign type is required. Select one or more campaign types.');
    }
  }
  return _TL_('');
};

const choiceOptionStyle = {
  root: {
    '& > div > div > label': {
      padding: '0',
    },
  },
};

export function validateScope({ selectedScope, selectedCampaignTypes, selectedCampaigns }) {
  const isTypeValid = _.isEmpty(getErrorMessage(selectedCampaignTypes, selectedScope));
  const isCampaignSelectionValid = selectedScope === 'specific' ? campaignSelectionValidator(selectedCampaigns) : true;

  return isTypeValid && isCampaignSelectionValid;
}

function SeasonalityScopeChoice(props) {
  const {
    classes,
    i18n,
    selectedScope,
    setSelectedScope,
    selectedCampaignTypes,
    selectedCampaigns,
    onSelectCampaigns,
    onSelectedCampaignTypeChanged,
    showValidationError,
    adjustmentType,
    permissions,
  } = props;

  const errorMessage = useMemo(
    () => (showValidationError ? i18n.getString(getErrorMessage(selectedCampaignTypes, selectedScope)) : ''),
    [i18n, selectedCampaignTypes, selectedScope, showValidationError]
  );

  const options = useMemo(() => [
    {
      key: 'type',
      text: i18n.getString(_TL_('Campaign type')),
      onRenderLabel: () => (
        <React.Fragment>
          <span className="ms-ChoiceFieldLabel" >
            {i18n.getString(_TL_('Campaign type'))}
          </span>
          <HelpPopupInline
            topic={isDataExclusion(adjustmentType) ? 'pop_BA_DataExclusions_CampaignType' : 'pop_BA_SeasonalityAdjustments_CampaignType'}
            ariaLabel={i18n.getString(_TL_('Click to learn more about {{item}}', { item: i18n.getString(_TL_('Campaign type')) }))}
          />
        </React.Fragment>
      ),
      onRenderField: (optionProps, render) => (
        <div className="campaignTypeCheckbox">
          {optionProps && render(optionProps)}
          {optionProps.checked &&
            (
              <div className={`checkbox-scope ${classes.scopeCheckbox}`}>
                <Checkbox
                  className="checkbox-search"
                  onChange={onSelectedCampaignTypeChanged}
                  label={i18n.getString(_TL_('Search'))}
                  name="Default"
                  checked={selectedCampaignTypes.find(type => type === 'Default')}
                />
                <Checkbox
                  className="checkbox-shopping"
                  onChange={onSelectedCampaignTypeChanged}
                  label={i18n.getString(_TL_('Shopping'))}
                  name="Shopping"
                  checked={selectedCampaignTypes.find(type => type === 'Shopping')}
                />
                {
                  permissions.dynamic.PerformanceMaxCampaigns &&
                  <Checkbox
                    className="checkbox-performanceMax"
                    onChange={onSelectedCampaignTypeChanged}
                    label={i18n.getString(_TL_('Performance Max'))}
                    name="PerformanceMax"
                    checked={selectedCampaignTypes.find(type => type === 'PerformanceMax')}
                  />
                }
                {
                  permissions.IsDataExclusionForAudienceCampaignEnabled && isDataExclusion(adjustmentType) &&
                  <Checkbox
                    className="checkbox-audience"
                    onChange={onSelectedCampaignTypeChanged}
                    label={i18n.getString(_TL_('Audience'))}
                    name="Audience"
                    checked={selectedCampaignTypes.find(type => type === 'Audience')}
                  />
                }
                <Error message={errorMessage} />
              </div>
            )
          }
        </div>
      ),
      styles: choiceOptionStyle,
    },
    {
      key: 'specific',
      text: i18n.getString(_TL_('Specific campaigns')),
      onRenderField: (optionProps, render) => (
        <div className="specificCampaignsCheckbox">
          {optionProps && render(optionProps)}
          {optionProps.checked && (
            <SeasonalityCampaignSelector
              selectedCampaigns={selectedCampaigns}
              onSelectCampaigns={onSelectCampaigns}
              showValidationError={showValidationError}
              i18n={i18n}
              adjustmentType={adjustmentType}
            />
          )}
        </div>
      ),
      styles: choiceOptionStyle,
    },
  ], [classes.scopeCheckbox, errorMessage, i18n, onSelectCampaigns, onSelectedCampaignTypeChanged, selectedCampaignTypes, selectedCampaigns, showValidationError, adjustmentType, permissions.dynamic.PerformanceMaxCampaigns]);

  const onChange = (_event, option) => {
    setSelectedScope(option.key);
  };

  return (
    <div className={`choice-scope ${classes.scopeChoices}`}>
      <Label id="seasonality-scope-label">
        {i18n.getString(_TL_('Scope'))}
        <HelpPopupInline
          topic={isDataExclusion(adjustmentType) ? 'pop_BA_DataExclusions_Scope' : 'pop_BA_SeasonalityAdjustments_Scope'}
          ariaLabel={i18n.getString(_TL_('Click to learn more about {{item}}', { item: i18n.getString(_TL_('Scope')) }))}
        />
      </Label>
      <ChoiceGroup
        className="choicegroup-scope"
        defaultSelectedKey="type"
        selectedKey={selectedScope}
        options={options}
        onChange={onChange}
        ariaLabelledBy="seasonality-scope-label"
      />
    </div>
  );
}

SeasonalityScopeChoice.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  selectedCampaignTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedScope: PropTypes.func.isRequired,
  selectedScope: PropTypes.string.isRequired,
  showValidationError: PropTypes.bool.isRequired,
  onSelectedCampaignTypeChanged: PropTypes.func.isRequired,
  selectedCampaigns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onSelectCampaigns: PropTypes.func.isRequired,
  adjustmentType: PropTypes.oneOf([AdjustmentType.DataExclusion, AdjustmentType.SeasonalityAdjustment]).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      PerformanceMaxCampaigns: PropTypes.bool,
    }),
  }).isRequired,
};

const Wrapped = _.compose(Comp => withDefaultStyles(Comp, getStyles), withPermissions)(SeasonalityScopeChoice);

export { Wrapped as SeasonalityScopeChoice };
